import { useQuery } from "@tanstack/react-query";
import { ApiError } from "./api.ts";
import { listMembers, TEAM_MEMBERS_KEY } from "./management-client.ts";

/**
 * The current team's members (`GET /api/v1/team/members`, tenant resolved server-side from the
 * session). Shared by the Members page "Team members" panel and the IDP service dashboard so both
 * read the SAME query key ({@link TEAM_MEMBERS_KEY}), fetcher and options, and a team switch /
 * write invalidation refreshes both.
 *
 * Fail fast: team-member management is a forwarded `/api/v1/team/*` route. If the backend route is
 * not available (missing/404) or the control plane is cold, do NOT sit in the default `waking`
 * retry loop that leaves a screen spinning. One quick retry, then surface a clear message (see
 * {@link membersErrorMessage}).
 */
export function useTeamMembers() {
  return useQuery({
    queryKey: TEAM_MEMBERS_KEY,
    queryFn: ({ signal }) => listMembers(signal),
    staleTime: 15_000,
    retry: 1,
    retryDelay: 800,
  });
}

/**
 * The message for a failed members read. Member management is a forwarded `/api/v1/team/*` route; if
 * the backend does not serve it yet (`not_found`/`malformed`) we say so plainly instead of a generic
 * error or an endless spinner. A still-waking core after the capped retry gets its own message.
 */
export function membersErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.kind === "not_found" || error.kind === "malformed") {
      return "Member management is not available yet. This control plane build does not serve the team directory.";
    }
    if (error.kind === "waking") {
      return "The control plane is still waking up. Try again in a moment.";
    }
    if (error.kind === "forbidden") return "You are not permitted to view team members.";
    if (error.kind === "network") return "We could not reach the control plane. Check your connection.";
  }
  return "Could not load team members.";
}
